import { useCallback, useEffect, useState } from 'react';
import type { MediaItem, SiteContext } from '@icetus/contracts';
import { Badge, Button, Card, formatBytes } from '@icetus/ui';
import { deleteMedia, fetchMedia, fetchSites, regenerateMediaVariants } from '../api';
import { ImageUploader } from '../components/image-uploader';
import { useConfirm } from '../components/confirm';
import type { LoadPhase } from '../lib/load-phase';

/**
 * 媒体库。
 *
 * 存在的理由很朴素：客户需要"看一眼我传过哪些图"。没有这一页，商品图集
 * 只能靠粘贴外链，而外贸客户手上通常只有一堆相机原图。
 *
 * 删除是**软删**（服务端保留对象 30 天）。所以文案写"移除"而不是"永久删除" ——
 * 说成永久删除会让人不敢点，说成移除又不解释清楚，则会有人误以为还能反悔很久。
 *
 * 缩略图（派生图）是**上传之后异步生成**的，所以这里必须能表达"还没生成好"：
 * 否则客户刚传完看到列表里那一条没有任何说明，第二天再看它还是那样，
 * 只会以为是功能坏了 —— 实际上只是后台排队慢了几秒。
 */

const PAGE_SIZE = 24;

/** 有图还在生成时，隔多久自动刷一次列表。3 秒：慢于编码本身，又快到不用手动刷新 */
const POLL_MS = 3000;

export function MediaPage({ tenantId }: { tenantId: string }) {
  const [sites, setSites] = useState<SiteContext[]>([]);
  const [siteId, setSiteId] = useState('');
  const [items, setItems] = useState<MediaItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState('');
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState<LoadPhase>('loading');
  const [copied, setCopied] = useState<string | null>(null);
  const { confirm, confirmDialog } = useConfirm();

  useEffect(() => {
    let cancelled = false;
    fetchSites(tenantId)
      .then((list) => {
        if (cancelled) return;
        setSites(list);
        setSiteId((prev) => (list.some((s) => s.site.id === prev) ? prev : (list[0]?.site.id ?? '')));
        // 一条站点都没有：不是"正在加载"，是真没东西可查。不标的话骨架永远转
        if (list.length === 0) setPhase('ready');
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message);
        // 连站点都取不到，图片列表必然也发不出请求
        setPhase('error');
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  /**
   * 搜索防抖。
   *
   * 300ms：比人敲字的间隔长，又短到不觉得卡。没有它的话，输入「2026」会打
   * 出 4 次查询，前 3 次的结果刚渲染就被覆盖 —— 列表会闪，光标也容易跳。
   */
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setQuery(keyword.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [keyword]);

  /**
   * `silent` 用于轮询：不能把 busy 立起来，否则每隔 3 秒整页的按钮会闪一下
   * "禁用"，客户正要去点「移除」时按钮突然不可点，会以为是页面卡了。
   *
   * 上传完、删完、重排缩略图之后的刷新也走 silent —— 手里这一屏图还在，
   * 退回骨架等于让客户每次操作后都看一遍"重新加载"，比多等半秒更烦人。
   */
  const reload = useCallback(
    async (silent = false) => {
      // 站点还没定下来时不动 phase：那会儿标 ready 就等于第一帧写上「还没有图片」
      if (!siteId) return;
      if (!silent) {
        setBusy(true);
        setPhase('loading');
      }
      try {
        const data = await fetchMedia(tenantId, siteId, page, PAGE_SIZE, query);
        setItems(data.items);
        setTotal(data.total);
        setError(null);
        setPhase('ready');
      } catch (err) {
        setError((err as Error).message);
        // 轮询失败不改 phase：手里这一屏图还在，为一次后台刷新把整块换成
        // 错误页是夸张的；提示条挂一下、下一轮自动重试就够了
        if (!silent) setPhase('error');
      } finally {
        if (!silent) setBusy(false);
      }
    },
    [tenantId, siteId, page, query],
  );

  useEffect(() => {
    void reload();
  }, [reload]);

  /**
   * 换站点时先把上一站的图清掉。
   *
   * 不清的话，新站点的列表回来之前，屏幕上挂的是**别家**的图 —— 这时点
   * 「移除」删掉的就是另一个站点的素材，而界面上完全看不出这件事。
   * 几项状态一次性改完，只触发一轮渲染、只发一次请求。
   */
  const switchSite = (next: string) => {
    if (next === siteId) return;
    setSiteId(next);
    setPage(1);
    setItems([]);
    setTotal(0);
    setPhase('loading');
  };

  /** 失败后重试：先撤掉红条，否则重试成功前它一直挂着 */
  const retry = () => {
    setError(null);
    void reload();
  };

  /**
   * 有图还在生成时自动刷新。
   *
   * 只在该页确实存在 pending 时才起定时器：否则每 3 秒拉一次列表，
   * 一个客户开着页面去吃饭，回来时后端日志里全是他一个人的请求。
   * 依赖 items 而不是挂在原地 —— 全部生成完，定时器自然就不再被创建。
   */
  useEffect(() => {
    if (!items.some((item) => item.variantsStatus === 'pending')) return;
    const timer = window.setTimeout(() => void reload(true), POLL_MS);
    return () => window.clearTimeout(timer);
  }, [items, reload]);

  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(url);
      // 2 秒后清掉提示：一直挂着会分不清"刚复制过"和"还在复制中"
      window.setTimeout(() => setCopied((prev) => (prev === url ? null : prev)), 2000);
    } catch {
      setError('浏览器不允许写剪贴板，请手动选中链接复制');
    }
  };

  const remove = async (item: MediaItem) => {
    // 用缩略图代替文件名：客户要确认的是"删的是哪张"，看一眼比读一串
    // 微信图片_20250828171604_18.jpg 快得多
    const ok = await confirm({
      title: '移除这张图？',
      tone: 'danger',
      confirmText: '移除',
      message: (
        <>
          <img
            className="confirm__thumb"
            src={item.url}
            alt={item.alt ?? item.originalName ?? ''}
          />
          {/*
            一句话说完后果。原来分成「将从媒体库移除，引用它的页面会显示不出图」
            与「目前没有内容在用它 / 正被 N 处使用」两段，前半段是空话
            （对话框标题和按钮已经写着"移除"），真正要判断的只有一件事：
            这张图还有没有别人在用。
          */}
          <p>
            {item.usageCount
              ? `这张图正被 ${item.usageCount} 处内容使用（页面区块、商品、分类或站点 Logo），移除后那几处会显示不出图。`
              : '这张图目前没有内容在用，移除不会影响任何页面。'}
          </p>
          <p className="muted">
            对象会保留 30 天再物理删除，期间链接仍然可以打开。这是刻意的：
            删错一次不用重传原图。
          </p>
        </>
      ),
    });
    if (!ok) return;
    setBusy(true);
    try {
      await deleteMedia(tenantId, item.id);
      setNotice('已移除');
      await reload(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const regenerate = async (item: MediaItem) => {
    setBusy(true);
    try {
      await regenerateMediaVariants(tenantId, item.id);
      setNotice('已重新排队生成缩略图');
      await reload(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // 不再写卡片标题与说明：顶栏标题已经是「媒体库」，而"图片会发布到公开地址"
  // 这句对已经会用的人来说是每次都要划过的噪音。
  return (
    <Card>
      {error ? <p className="error">{error}</p> : null}
      {notice ? <p className="notice">{notice}</p> : null}

      <div className="toolbar">
        <label className="stack stack--inline">
          <span>站点</span>
          <select
            className="field"
            value={siteId}
            onChange={(e) => switchSite(e.target.value)}
          >
            {sites.map((site) => (
              <option key={site.site.id} value={site.site.id}>
                {site.site.name}
              </option>
            ))}
          </select>
        </label>
        <input
          className="field media-search"
          value={keyword}
          placeholder="搜索文件名…"
          aria-label="搜索媒体库"
          onChange={(e) => setKeyword(e.target.value)}
        />
        {siteId ? (
          <ImageUploader
            tenantId={tenantId}
            siteId={siteId}
            disabled={busy}
            variant="primary"
            onUploaded={async (uploaded) => {
              setNotice(
                uploaded.length === 1 ? '已上传 1 张' : `已上传 ${uploaded.length} 张`,
              );
              setPage(1);
              await reload(true);
            }}
          />
        ) : null}
        {/* 还在加载就不报数字：原来这里先写「共 0 张」，
            和下面那句「还没有图片」是同一类误导 */}
        <span className="muted media-total">
          {phase === 'loading' ? '加载中…' : phase === 'error' ? '加载失败' : `共 ${total} 张`}
        </span>
      </div>

      {phase === 'loading' ? (
        <MediaGridSkeleton />
      ) : phase === 'error' ? (
        <div className="empty">
          <p className="empty__title">图片没能加载出来</p>
          <p className="muted">
            网络不稳或服务暂时不可用，和你的素材无关。重新加载一次通常就好了。
          </p>
          <Button variant="ghost" onClick={retry} disabled={busy}>
            {busy ? '正在重新加载…' : '重新加载'}
          </Button>
        </div>
      ) : items.length === 0 ? (
        <p className="muted">
          {query
            ? `没有文件名含「${query}」的图片。清空搜索看看全部素材。`
            : `还没有图片。${siteId ? '点「上传图片」开始，单张不超过 10MB。' : '先在控制台创建一个站点。'}`}
        </p>
      ) : (
        <div className="media-grid">
          {items.map((item) => (
            <figure className="media-card" key={item.id}>
              <a className="media-card__thumb" href={item.url} target="_blank" rel="noreferrer">
                <img src={item.url} alt={item.alt ?? item.originalName ?? ''} loading="lazy" />
              </a>
              <figcaption className="media-card__body">
                <div className="media-card__name" title={item.originalName ?? item.objectKey}>
                  {item.originalName ?? item.objectKey.split('/').pop()}
                </div>
                <div className="muted media-card__meta">
                  {formatBytes(item.bytes)}
                  {item.width && item.height ? ` · ${item.width}×${item.height}` : ''}
                  {' · '}
                  {new Date(item.createdAt).toLocaleDateString('zh-CN')}
                </div>
                {/*
                  派生图状态。ready 什么都不显示（那是正常态，一行绿色只会让列表变吵），
                  其余三种都要说清楚，否则客户看到的就是"传上去了但前台有点糊"，
                  而他根本不知道还有"正在生成"这回事。
                */}
                {item.variantsStatus === 'pending' ? (
                  <div className="media-card__meta">
                    <Badge tone="neutral">缩略图生成中…</Badge>
                  </div>
                ) : null}
                {item.variantsStatus === 'failed' ? (
                  <div className="media-card__meta">
                    <Badge tone="danger">缩略图生成失败</Badge>
                    {item.variantsError ? (
                      <span className="muted media-card__hint" title={item.variantsError}>
                        {item.variantsError}
                      </span>
                    ) : null}
                  </div>
                ) : null}
                {item.variantsStatus === 'skipped' ? (
                  <div className="media-card__meta">
                    {/* 说清楚"为什么没有"，而不是让客户以为漏了它 */}
                    <Badge tone="neutral">无需缩略图</Badge>
                  </div>
                ) : null}
                {item.usageCount && item.usageCount > 0 ? (
                  <div className="media-card__meta">
                    <Badge tone="neutral">
                      被 {item.usageCount} 处使用
                    </Badge>
                  </div>
                ) : null}
                <div className="media-card__actions">
                  {/* 这张卡上最常用的动作是把地址拷走，所以给它品牌色，
                      和旁边两个灰色的「重新生成 / 移除」分开 */}
                  <Button
                    variant="ghost"
                    className="media-card__copy"
                    onClick={() => void copy(item.url)}
                  >
                    {copied === item.url ? '已复制' : '复制链接'}
                  </Button>
                  {item.variantsStatus === 'failed' || item.variantsStatus === 'pending' ? (
                    <Button variant="ghost" disabled={busy} onClick={() => void regenerate(item)}>
                      重新生成
                    </Button>
                  ) : null}
                  <Button variant="ghost" disabled={busy} onClick={() => void remove(item)}>
                    移除
                  </Button>
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      )}

      {/* 失败时不显示分页：那几行数字是上一轮的结果，留着会让人以为当前页还有内容 */}
      {totalPages > 1 && phase !== 'error' ? (
        <div className="toolbar toolbar--end">
          <Button variant="ghost" disabled={page <= 1 || busy} onClick={() => setPage((p) => p - 1)}>
            上一页
          </Button>
          <span className="muted">
            {page} / {totalPages}
          </span>
          <Button
            variant="ghost"
            disabled={page >= totalPages || busy}
            onClick={() => setPage((p) => p + 1)}
          >
            下一页
          </Button>
        </div>
      ) : null}

      {confirmDialog}
    </Card>
  );
}

/**
 * 图片网格骨架。
 *
 * 直接复用 `.media-card` 外壳（边框、圆角、4:3 缩略图区），只把内容换成灰块：
 * 形状、行高、间距都与真实卡片一致，数据回来时网格不会重排。
 * 铺 8 格而不是整页 24 格 —— 它要说明"正在来"，不是预告数量。
 */
function MediaGridSkeleton() {
  return (
    <div className="media-grid" role="status" aria-live="polite">
      <span className="visually-hidden">正在加载图片…</span>
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div className="media-card" key={i} aria-hidden="true">
          <span className="skeleton skeleton--media-thumb" />
          <div className="media-card__body">
            <span className="skeleton skeleton--media-name" />
            <span className="skeleton skeleton--media-meta" />
            {/* 操作按钮也占位：真实卡片底下还有一行「复制链接 / 移除」，
                不给它留高度，数据回来时每张卡会突然长高一截 */}
            <div className="media-card__actions">
              <span className="skeleton skeleton--media-btn" />
              <span className="skeleton skeleton--media-btn skeleton--media-btn--short" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
